import { AttributionModel as AttributionPreset } from "@redotech/cockroachdb-codegen/tables/attribution/attribution-model";
import { durationToSeconds, TimeUnit } from "@redotech/redo-common-model/time";
import * as z from "zod";

export { AttributionPreset };

/** Subset of TimeUnit values the attribution UI exposes. */
export const ATTRIBUTION_TIME_UNITS = [
  TimeUnit.MINUTE,
  TimeUnit.HOUR,
  TimeUnit.DAY,
] as const;

/**
 * The longest attribution window, everywhere. Persistence rejects anything
 * larger and the analytics filters stop here. Raise it when merchants ask.
 */
export const MAX_ATTRIBUTION_WINDOW_DAYS = 30;

/** Time window looked backward from an effect event. */
export const durationSchema = z
  .object({
    value: z.number().int().positive(),
    unit: z.enum(ATTRIBUTION_TIME_UNITS),
  })
  .refine(
    (d) =>
      durationToSeconds(d.value, d.unit) <=
      MAX_ATTRIBUTION_WINDOW_DAYS * 24 * 60 * 60,
    { message: `window exceeds ${MAX_ATTRIBUTION_WINDOW_DAYS} days` },
  );
export type Duration = z.infer<typeof durationSchema>;

/** Things we want to take credit for. */
export enum AttributionEffectEventType {
  ORDER = "order",
}

/** A way a touchpoint can qualify for an order's credit. */
export enum AttributionModel {
  /** The cause event occurred within its window before the order. */
  INFERRED = "inferred",
  /** A provable per-touchpoint signal links it to the order. */
  DIRECT = "direct",
}

/** Which qualifiers to union for a run; at least one must be true. */
export type QualifierSelection = Record<AttributionModel, boolean>;

/** How a qualifying order's credit is split among its touchpoints. */
export enum AttributionDistribution {
  /** Most recent qualifying touchpoint gets full credit. */
  LAST_TOUCH = "last_touch",
  /** Qualifying touchpoints split credit evenly. */
  MULTI_TOUCH_LINEAR = "linear",
}

/**
 * Merchant-facing preset persisted as the default report view. Resolves to the
 * query axes via {@link ATTRIBUTION_MODEL_RESOLUTION}.
 */
export interface AttributionAxes {
  qualifiers: QualifierSelection;
  distribution: AttributionDistribution;
}

export const ATTRIBUTION_MODEL_RESOLUTION: Record<
  AttributionPreset,
  AttributionAxes
> = {
  [AttributionPreset.LastTouch]: {
    qualifiers: {
      [AttributionModel.INFERRED]: true,
      [AttributionModel.DIRECT]: false,
    },
    distribution: AttributionDistribution.LAST_TOUCH,
  },
  [AttributionPreset.Linear]: {
    qualifiers: {
      [AttributionModel.INFERRED]: true,
      [AttributionModel.DIRECT]: false,
    },
    distribution: AttributionDistribution.MULTI_TOUCH_LINEAR,
  },
  [AttributionPreset.Direct]: {
    qualifiers: {
      [AttributionModel.INFERRED]: false,
      [AttributionModel.DIRECT]: true,
    },
    distribution: AttributionDistribution.LAST_TOUCH,
  },
};

/**
 * UI grouping of cause events. Also used as the keys for direct attribution config.
 */
export enum AttributionTouchpoint {
  RECOVER = "recover",
  EMAIL = "email",
  SMS = "sms",
}

/** Things that cause us to take credit. */
export enum AttributionCauseEventType {
  // Email
  EMAIL_SEND = "email_send",
  EMAIL_DELIVERED = "email_delivered",
  EMAIL_OPEN = "email_open",
  EMAIL_CLICK = "email_click",
  // SMS — SMS_DELIVERED and SMS_OPEN intentionally omitted: carriers don't
  // reliably report either, so they'd be misleading qualifying signals.
  SMS_SEND = "sms_send",
  SMS_CLICK = "sms_click",
  // Recover (SMS conversations, category='Recover')
  RECOVER_MESSAGE_SENT = "recover_message_sent",
  RECOVER_MESSAGE_RECEIVED = "recover_message_received",
  RECOVER_CLICK = "recover_click",
}

export enum ComparisonOperator {
  GREATER_THAN_OR_EQUAL = "gte",
  LESS_THAN_OR_EQUAL = "lte",
  EQUAL = "eq",
}

/**
 * Within-touchpoint count check. Compiles to a HAVING on the touchpoint
 * aggregation only when `enabled` is true; otherwise the predicate is a
 * plain "any matching event exists in the window". `operator`/`n` are
 * preserved when disabled so the UI can remember the last threshold.
 */
export const ruleCountSchema = z.object({
  enabled: z.boolean(),
  operator: z.enum(ComparisonOperator),
  n: z.number().int().positive(),
});
export type RuleCount = z.infer<typeof ruleCountSchema>;

/** Per-cause-event qualifying config. Disabled entries don't contribute. */
export const causeEventConfigSchema = z.object({
  enabled: z.boolean(),
  window: durationSchema,
  count: ruleCountSchema,
});
export type CauseEventConfig = z.infer<typeof causeEventConfigSchema>;

/**
 * Per-touchpoint config for the DIRECT model.
 */
export const directConfigSchema = z.object({
  enabled: z.boolean(),
  window: durationSchema,
});
export type DirectConfig = z.infer<typeof directConfigSchema>;

/**
 * One optional config per enum value: unknown keys drop and missing keys stay unset, so a
 * deploy that widens the enum can't fail old pods or tabs. The repo fills unset keys on read.
 */
function configPerEnumValue<E extends string, V extends z.ZodType>(
  values: readonly E[],
  valueSchema: V,
) {
  return z.object(
    Object.fromEntries(
      values.map((value) => [value, valueSchema.optional()]),
    ) as { [K in E]: z.ZodOptional<V> },
  );
}

/** Attribution config for one effect. Every effect shares this shape. */
export const effectAttributionConfigSchema = z.object({
  /** Which model loads first in reports. Other models may still be computed on demand for the same data. */
  defaultModel: z.enum(AttributionPreset),
  /**
   * Inferred attribution (last-touch / multi-touch linear): credit is inferred from a
   * cause event landing within a window.
   */
  inferred: configPerEnumValue(
    Object.values(AttributionCauseEventType),
    causeEventConfigSchema,
  ),
  /**
   * Direct attribution: a provable per-touchpoint signal linking the effect to
   * the touchpoint. Less granular than inferred (touchpoint-level).
   */
  direct: configPerEnumValue(
    Object.values(AttributionTouchpoint),
    directConfigSchema,
  ),
});
export type EffectAttributionConfig = z.infer<
  typeof effectAttributionConfigSchema
>;

/** Per-team attribution settings: the same config shape for every effect kind. */
export const attributionSettingsSchema = z.record(
  z.enum(AttributionEffectEventType),
  effectAttributionConfigSchema,
);
export type AttributionSettings = z.infer<typeof attributionSettingsSchema>;
